package app.nestbridge.feature.invitations.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.OtpVerificationResult
import app.nestbridge.app.context.identity.UserContext
import app.nestbridge.app.context.identity.UserContextProvider
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.careteam.screen.CreateCareTeamScreen
import app.nestbridge.feature.careteam.screen.TeamCreationResult
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.screen.InvitationConfirmationScreen
import app.nestbridge.feature.invitations.screen.InvitationDetailEvent
import app.nestbridge.feature.invitations.screen.InvitationDetailScreen
import app.nestbridge.feature.invitations.screen.InvitationDetailState
import app.nestbridge.feature.invitations.screen.InvitationOtpVerificationScreen
import app.nestbridge.feature.invitations.screen.InvitationResult
import app.nestbridge.feature.invitations.screen.InvitationScreenStatus
import app.nestbridge.feature.invitations.usecase.AcceptInvitationUseCase
import app.nestbridge.feature.invitations.usecase.DeclineInvitationUseCase
import app.nestbridge.feature.invitations.usecase.FindInvitationByIdUseCase
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileResult
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileScreen
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(screen = InvitationDetailScreen::class, scope = AppScope::class)
@Inject
class InvitationDetailPresenter(
    @Assisted private val navigator: Navigator,
    @Assisted private val screen: InvitationDetailScreen,
    private val userContextProvider: UserContextProvider,
    private val findInvitationByIdUseCase: FindInvitationByIdUseCase,
    private val acceptInvitationUseCase: AcceptInvitationUseCase,
    private val declineInvitationUseCase: DeclineInvitationUseCase,
) : Presenter<InvitationDetailState> {

    @Composable
    override fun present(): InvitationDetailState {
        val scope = rememberCoroutineScope()
        val userContext by userContextProvider.context.collectAsState()
        var invitation by remember { mutableStateOf(Invitation.empty) }
        var status by remember { mutableStateOf(InvitationScreenStatus.INITIAL) }

        val careTeamNavigator = rememberAnsweringNavigator<TeamCreationResult>(navigator) { result ->
            if (result.status == TeamCreationResult.Status.Completed) {
                status = InvitationScreenStatus.CARE_TEAM_CREATED
            }
        }

        val professionalProfileNavigator = rememberAnsweringNavigator<CreateProfessionalProfileResult>(navigator) { result ->
            if (result.status == CreateProfessionalProfileResult.Status.Created) {
                status = InvitationScreenStatus.PROFESSIONAL_PROFILE_CREATED
            }
        }

        val otpNavigator = rememberAnsweringNavigator<OtpVerificationResult>(navigator) { result ->
            if (result.status == OtpVerificationResult.Status.Verified) {
                status = InvitationScreenStatus.OTP_VERIFIED
            }
        }

        val confirmationNavigator = rememberAnsweringNavigator<InvitationResult>(navigator) { result ->
             status = when (result.decision) {
                 InvitationResult.Decision.Accepted -> InvitationScreenStatus.ACCEPTED
                 InvitationResult.Decision.Declined -> InvitationScreenStatus.DECLINED
                 InvitationResult.Decision.Cancelled -> InvitationScreenStatus.INITIAL
             }
        }

        fun proceedWithAcceptance() {
            scope.launchSafely {
                acceptInvitationUseCase(screen.invitationId)
                navigator.pop(
                    InvitationResult(
                        invitationId = screen.invitationId,
                        decision = InvitationResult.Decision.Accepted,
                    ),
                )
            }
        }

        fun proceedWithDeclination() {
            scope.launchSafely {
                declineInvitationUseCase(screen.invitationId)
                navigator.pop(
                    InvitationResult(
                        invitationId = screen.invitationId,
                        decision = InvitationResult.Decision.Declined,
                    ),
                )
            }
        }

        fun handleCareTeamStep() {
            if (invitation.requiresCareTeamCreation()) {
                careTeamNavigator.goTo(
                    CreateCareTeamScreen(childId = invitation.childId)
                )
            } else {
                proceedWithAcceptance()
            }
        }

        fun handleProfessionalProfileStep() {
            val context = userContext ?: return
            if (invitation.requiresProfessionalProfileCreation(context)) {
                professionalProfileNavigator.goTo(
                    CreateProfessionalProfileScreen(childId = invitation.childId),
                )
            } else {
                handleCareTeamStep()
            }
        }

        fun handleVerificationStep() {
            val context = userContext ?: return
            if (invitation.requiresVerification(context)) {
                otpNavigator.goTo(
                    InvitationOtpVerificationScreen(
                        invitationId = screen.invitationId,
                        channel = when (invitation.deliveryMethod) {
                            InvitationDeliveryMethod.EMAIL -> OtpChannel.Email
                            InvitationDeliveryMethod.PHONE -> OtpChannel.Phone
                        },
                        destination = invitation.deliveryDestination ?: "",
                    ),
                )
            } else {
                handleProfessionalProfileStep()
            }
        }

        val sink: (InvitationDetailEvent) -> Unit = { event ->
            when (event) {
                InvitationDetailEvent.OnAppear -> {
                    scope.launchSafely {
                        invitation = findInvitationByIdUseCase(screen.invitationId).getOrNull()
                            ?: error("Unable to find invitation with id '${screen.invitationId}'")
                        when (status) {
                            InvitationScreenStatus.INITIAL -> Unit
                            InvitationScreenStatus.ACCEPTED -> handleVerificationStep()
                            InvitationScreenStatus.OTP_VERIFIED -> handleProfessionalProfileStep()
                            InvitationScreenStatus.PROFESSIONAL_PROFILE_CREATED -> handleCareTeamStep()
                            InvitationScreenStatus.CARE_TEAM_CREATED -> proceedWithAcceptance()
                            InvitationScreenStatus.DECLINED ->  proceedWithDeclination()
                        }
                    }
                }

                InvitationDetailEvent.Accept -> confirmationNavigator.goTo(
                    InvitationConfirmationScreen(
                        invitationId = screen.invitationId,
                        mode = InvitationConfirmationScreen.Mode.Accept,
                    ),
                )

                InvitationDetailEvent.Decline -> confirmationNavigator.goTo(
                    InvitationConfirmationScreen(
                        invitationId = screen.invitationId,
                        mode = InvitationConfirmationScreen.Mode.Decline,
                    ),
                )

                InvitationDetailEvent.DecideLater -> navigator.pop(
                    InvitationResult(
                        invitationId = screen.invitationId,
                        decision = InvitationResult.Decision.Cancelled,
                    ),
                )
            }
        }

        return InvitationDetailState(
            invitation = invitation,
            requiresVerification = userContext?.let { invitation.requiresVerification(it) } ?: false,
            eventSink = sink,
        )
    }
}

private fun Invitation.requiresVerification(userContext: UserContext): Boolean {
    val destination = deliveryDestination ?: return false
    return !userContext.user.emails.contains(destination) && !userContext.user.phoneNumbers.contains(destination)
}

private fun Invitation.requiresProfessionalProfileCreation(userContext: UserContext): Boolean {
    return (proposedTeamRole == TeamRelationshipRole.ORGANIZER ||
            proposedTeamRole == TeamRelationshipRole.THERAPIST) &&
            userContext.professionalProfile == null
}

private fun Invitation.requiresCareTeamCreation(): Boolean {
    return proposedTeamRole == TeamRelationshipRole.ORGANIZER && teamId == null
}
